import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { searchAddresses } from "@shared-kernel/application/use-cases/address-search/search-addresses";
import { selectAddressSearchRetrieval } from "@shared-kernel/application/use-cases/address-search/selectors/address-search-selectors";
import type { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";

export function useAddressSearch(country: SUPPORTED_COUNTRY) {
  const dispatch = useAppDispatch();
  const { data: suggestions, fetching } = useAppSelector(selectAddressSearchRetrieval);

  const [query, setQuery] = useState("");
  const [lastFetchedKey, setLastFetchedKey] = useState("");
  const sessionTokenRef = useRef(crypto.randomUUID());

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = crypto.randomUUID();
  }, []);

  useEffect(() => {
    if (query.length < 3) {
      return;
    }

    const key = `${country}::${query}`;
    const timeout = setTimeout(() => {
      dispatch(searchAddresses({ query, sessionToken: sessionTokenRef.current, country })).finally(() => {
        setLastFetchedKey(key);
      });
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [query, dispatch, country]);

  const hasQuery = query.length >= 3;
  const isStale = hasQuery && lastFetchedKey !== `${country}::${query}`;
  const isLoading = hasQuery && (isStale || fetching === "pending");

  return {
    query,
    setQuery,
    suggestions: hasQuery && !isStale ? suggestions : [],
    isLoading,
    sessionToken: sessionTokenRef.current,
    resetSessionToken,
  };
}
